// Pronunciation guide for the phoneme codes Azure returns (en-US, ARPABET-style
// lower case: "th", "ih", "ng"…). Every coaching line on the result pages is
// built from this table, so it is deterministic: no model, no cost, nothing
// made up.
//
//   ipa     IPA symbol
//   say     learner respelling chunk ("think" → th·i·ng·k → "thingk")
//   ex      a common word with the sound
//   tip     what the mouth does — one sentence, imperative
//   slip    the usual mistake, phrased as what it ends up sounding like

export const SOUNDS = {
  // vowels
  iy: { ipa: 'iː', say: 'ee', ex: 'see', vowel: true, tip: 'Smile a little and hold it long — tongue high and forward.', slip: 'cut short, so “sheep” sounds like “ship”' },
  ih: { ipa: 'ɪ', say: 'i', ex: 'sit', vowel: true, tip: 'Short and relaxed — drop the jaw slightly from “ee” and don’t smile.', slip: 'stretched into “ee”, so “ship” sounds like “sheep”' },
  ey: { ipa: 'eɪ', say: 'ay', ex: 'day', vowel: true, tip: 'Start at “e” and glide up to “ee” — it moves, it’s two sounds.', slip: 'kept flat as a single “e”' },
  eh: { ipa: 'ɛ', say: 'e', ex: 'bed', vowel: true, tip: 'Mouth half open, tongue in the middle — short and crisp.', slip: 'opened too far towards “a” in “bad”' },
  ae: { ipa: 'æ', say: 'a', ex: 'cat', vowel: true, tip: 'Open the jaw wide and spread the lips — a flat, bright “a”.', slip: 'closed into “e”, so “bad” sounds like “bed”' },
  aa: { ipa: 'ɑː', say: 'ah', ex: 'father', vowel: true, tip: 'Drop the jaw, tongue low and back — like showing a doctor your throat.', slip: 'rounded into “o”' },
  ao: { ipa: 'ɔː', say: 'aw', ex: 'law', vowel: true, tip: 'Round the lips a little and keep the jaw open.', slip: 'closed into “o” in “go”' },
  ah: { ipa: 'ʌ', say: 'uh', ex: 'cup', vowel: true, tip: 'Short, relaxed, mouth slightly open — the lazy “uh” sound.', slip: 'pronounced as an open “a” or a round “o”' },
  ax: { ipa: 'ə', say: 'uh', ex: 'about', vowel: true, tip: 'Tiny and unstressed — barely there. Don’t give it a full vowel.', slip: 'given a full vowel, which makes the rhythm choppy' },
  uw: { ipa: 'uː', say: 'oo', ex: 'food', vowel: true, tip: 'Round the lips tight and hold it long.', slip: 'cut short, so “fool” sounds like “full”' },
  uh: { ipa: 'ʊ', say: 'oo', ex: 'book', vowel: true, tip: 'Short and loose — lips only slightly round.', slip: 'stretched into “oo”, so “full” sounds like “fool”' },
  ow: { ipa: 'oʊ', say: 'oh', ex: 'go', vowel: true, tip: 'Start at “o” and glide the lips rounder — it moves.', slip: 'kept flat as a single “o”' },
  ay: { ipa: 'aɪ', say: 'eye', ex: 'my', vowel: true, tip: 'Start wide open at “ah” and glide up to “ee”.', slip: 'flattened into a single “a”' },
  aw: { ipa: 'aʊ', say: 'ow', ex: 'now', vowel: true, tip: 'Start open at “ah” and round the lips towards “oo”.', slip: 'flattened into “o”' },
  oy: { ipa: 'ɔɪ', say: 'oy', ex: 'boy', vowel: true, tip: 'Start round at “aw” and glide to “ee”.', slip: 'kept as a single “o”' },
  er: { ipa: 'ɝ', say: 'ur', ex: 'bird', vowel: true, tip: 'Curl the tongue back a little and hold it — one smooth “ur”, no rolled r.', slip: 'split into a vowel plus a rolled r' },
  // consonants
  th: { ipa: 'θ', say: 'th', ex: 'think', tip: 'Put the tip of your tongue lightly between your teeth and blow — no voice.', slip: 'said as “t” or “s”, so “think” sounds like “tink” or “sink”' },
  dh: { ipa: 'ð', say: 'th', ex: 'this', tip: 'Tongue tip between the teeth, then add your voice — it should buzz.', slip: 'said as “d” or “z”, so “this” sounds like “dis” or “zis”' },
  r: { ipa: 'ɹ', say: 'r', ex: 'red', tip: 'Pull the tongue back and don’t let it touch anything — no tap, no roll.', slip: 'tapped or rolled, or swapped with “l”' },
  l: { ipa: 'l', say: 'l', ex: 'light', tip: 'Press the tongue tip on the ridge behind your top teeth and let air pass the sides.', slip: 'swapped with “r”, or dropped at the end of a word' },
  v: { ipa: 'v', say: 'v', ex: 'very', tip: 'Top teeth gently on the lower lip, then buzz.', slip: 'said as “w” or “b”, so “very” sounds like “wery” or “bery”' },
  w: { ipa: 'w', say: 'w', ex: 'we', tip: 'Round the lips like “oo” — no teeth touching.', slip: 'said as “v”, so “wine” sounds like “vine”' },
  f: { ipa: 'f', say: 'f', ex: 'fun', tip: 'Top teeth on the lower lip and blow — no voice.', slip: 'said as “p”' },
  p: { ipa: 'p', say: 'p', ex: 'pen', tip: 'Close the lips and pop them open with a puff of air.', slip: 'said as “b” without the puff of air' },
  b: { ipa: 'b', say: 'b', ex: 'bed', tip: 'Close the lips and release with your voice on.', slip: 'said as “p”' },
  t: { ipa: 't', say: 't', ex: 'top', tip: 'Tongue tip on the ridge behind the teeth, then release — and finish it at the end of words.', slip: 'dropped at the end of the word' },
  d: { ipa: 'd', say: 'd', ex: 'dog', tip: 'Like “t” but with your voice on — keep it at the end of words.', slip: 'dropped or turned into “t” at the end of the word' },
  k: { ipa: 'k', say: 'k', ex: 'cat', tip: 'Back of the tongue on the roof of the mouth, then release with air.', slip: 'dropped at the end of the word' },
  g: { ipa: 'g', say: 'g', ex: 'go', tip: 'Like “k” but with your voice on.', slip: 'said as “k”' },
  s: { ipa: 's', say: 's', ex: 'see', tip: 'Teeth close together, tongue behind them — a sharp hiss.', slip: 'dropped from plurals and verbs' },
  z: { ipa: 'z', say: 'z', ex: 'zoo', tip: 'Like “s” but buzzing — many plurals end with this sound.', slip: 'said as a hissing “s”' },
  sh: { ipa: 'ʃ', say: 'sh', ex: 'she', tip: 'Round the lips and push the tongue back a little from “s”.', slip: 'said as “s”' },
  zh: { ipa: 'ʒ', say: 'zh', ex: 'vision', tip: 'Like “sh” but with your voice on.', slip: 'said as “sh” or “j”' },
  ch: { ipa: 'tʃ', say: 'ch', ex: 'chair', tip: 'Start with “t” and release straight into “sh”.', slip: 'said as “sh”' },
  jh: { ipa: 'dʒ', say: 'j', ex: 'job', tip: 'Start with “d” and release straight into a buzzing “zh”.', slip: 'said as “y” or “zh”' },
  m: { ipa: 'm', say: 'm', ex: 'me', tip: 'Lips together and hum.', slip: 'dropped at the end of the word' },
  n: { ipa: 'n', say: 'n', ex: 'no', tip: 'Tongue tip on the ridge behind the teeth and hum.', slip: 'confused with “ng”' },
  ng: { ipa: 'ŋ', say: 'ng', ex: 'sing', tip: 'Back of the tongue up, air through the nose — don’t add a “g” after it.', slip: 'said as “n” or with an extra “g”' },
  h: { ipa: 'h', say: 'h', ex: 'hat', tip: 'Just a soft breath out before the vowel.', slip: 'dropped, or made rough like a throat sound' },
  y: { ipa: 'j', say: 'y', ex: 'yes', tip: 'Tongue high like “ee”, then slide into the vowel.', slip: 'said as “j”' },
};

const info = (ph) => SOUNDS[ph] || null;
export const soundInfo = info;

export const ipaOf = (phonemes = []) => `/${phonemes.map((p) => info(p)?.ipa || p).join('')}/`;

// Group phonemes into rough syllables (one vowel each; between two vowels a
// single consonant joins the next syllable, a cluster is split) and respell.
export const respell = (phonemes = []) => {
  const isV = (p) => info(p)?.vowel;
  const syllables = [];
  let cur = [];
  let pendingC = [];
  phonemes.forEach((p) => {
    if (isV(p)) {
      if (cur.length) {
        const move = pendingC.length > 1 ? pendingC.slice(1) : pendingC;
        cur.push(...pendingC.slice(0, pendingC.length - move.length));
        syllables.push(cur);
        cur = [...move, p];
      } else {
        cur = [...pendingC, p];
      }
      pendingC = [];
    } else {
      pendingC.push(p);
    }
  });
  if (cur.length || pendingC.length) syllables.push([...cur, ...pendingC]);
  return syllables
    .map((syl) => syl.map((p, i) => {
      const s = info(p)?.say || p;
      // "eye" reads naturally at the start of a syllable; after a consonant use "igh" (s·igh·d)
      if (p === 'ay' && i > 0) return 'igh';
      return s;
    }).join(''))
    .join('·');
};

// Which practice focus trains a given sound.
export const focusForSound = (ph) => {
  if (ph === 'th' || ph === 'dh') return 'TH sounds';
  if (ph === 'r' || ph === 'l') return 'R & L';
  if (ph === 'v' || ph === 'w') return 'V & W';
  if (info(ph)?.vowel) return 'Vowels';
  return 'Endings';
};
